import {
  AlignmentMeasurement,
  AngleMeasurement,
  PointMarkup,
  PolylineMeasurement,
  Markup,
  Measurement as Web3DMeasurement,
  PolygonMeasurement,
} from '@technology/web3d-plugin-markup';
import { Web3DViewer, Vector3, SnapPick } from '@technology/web3d';
import { UnreachableCaseError } from 'ts-essentials';
import * as uuid from 'uuid';
import {
  calculateDistance,
  ensureAlpha,
  convertMetersToMillimeters,
  SlopeFormatter,
} from '@trimble/utilities';
import { web3dColorToRgb } from '@trimble/web3d-utils';
import { convertConnectPointToViewerPoint } from '../views/camera-conversion';
import {
  isAreaMeasurement,
  isVolumeMeasurement,
  Measurement,
  MeasurementType,
  PointMeasurementSettings,
  SlopeFormat,
  VolumeMeasurementParameters,
  VolumeMeasurementResults,
  VolumeMeasurementType,
} from './measurement';
import type {
  PassCountMeasurementParameters,
  PassCountMeasurementResults,
  PassCountMeasurementType,
} from './pass-count-measurement';
import { SnapType } from '../snapping/snap-type';
import {
  Markup as ConnectMarkup,
  MarkupPickPointType,
} from '@data-access/connect/tc-tcps';

/**
 * Measurement markup types using generated Connect API types
 */
type MeasureMarkup = ConnectMarkup & {
  type: 'measure';
  position2X: number;
  position2Y: number;
  position2Z: number;
};

type PickPoint = MarkupPickPointType & {
  type: 'point';
};

type MeasureWithPickMarkup = ConnectMarkup & {
  type: 'measure_with_pick';
  start: PickPoint;
  end: PickPoint;
  unitType: string;
  quantityUnit: string;
  quantityUnitFormat: string;
};

type AngleMarkup = ConnectMarkup & {
  type: 'angle';
  start: PickPoint;
  end: PickPoint;
};

export type MeasurementMarkup =
  | MeasureMarkup
  | MeasureWithPickMarkup
  | AngleMarkup;

const MIN_POLYLINE_POINTS = 2;

export const VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX = 'volume-measurement-';

/** Tileset model id for the post-compute heatmap preview before the volume is saved. */
export const VOLUME_COMPUTE_PREVIEW_TILESET_MODEL_ID =
  'compute-measurement-result-tileset';

// Shared by draw-time labeling and view-restore label regeneration.
export const MEASUREMENT_LABEL_PREFIX: Record<MeasurementType, string> = {
  point: 'Point',
  line: 'Line',
  polyline: 'Line',
  angle: 'Angle',
  alignment: 'Alignment',
  volume: 'Volume',
  passCount: 'Pass Count',
  area: 'Area',
};

export function getLabelFromMarkup(
  markup: Pick<Markup, 'name'>,
  measurements: Measurement[]
): string {
  const labelFor = (type: MeasurementType): string => {
    const prefix = MEASUREMENT_LABEL_PREFIX[type];
    const filtered = measurements.filter((m) => m.type === type);
    if (filtered.length === 0) return `${prefix} 1`;

    const pattern = new RegExp(`^${prefix} (\\d+)$`);
    const usedNumbers = filtered
      .map((m) => m.label?.match(pattern)?.[1])
      .filter((n): n is string => n !== undefined)
      .map(Number);

    const next =
      usedNumbers.length > 0
        ? Math.max(...usedNumbers) + 1
        : filtered.length + 1;
    return `${prefix} ${String(next)}`;
  };

  switch (markup.name) {
    case 'pointMarkup':
      return labelFor('point');
    case 'angleMeasurement':
      return labelFor('angle');
    case 'measurement':
      return labelFor('line');
    case 'polylineMeasurement':
      return labelFor('polyline');
    case 'alignmentMeasurement':
      return labelFor('alignment');
    case 'volumeMeasurement':
      return labelFor('volume');
    case 'passCountMeasurement':
      return labelFor('passCount');
    case 'polygonMeasurement':
      return labelFor('area');
    default:
      return '';
  }
}

/**
 * Returns the next auto-incremented label for a new volume measurement
 * (e.g. "Volume 1", "Volume 2") based on existing measurements.
 * Delegates to getLabelFromMarkup so all label logic stays in one place.
 * Fake markup is used to avoid adding new logic to getLabelFromMarkup.
 */
export function getNextVolumeLabel(measurements: Measurement[]): string {
  return getLabelFromMarkup({ name: 'volumeMeasurement' }, measurements);
}

/**
 * Returns the next auto-incremented label for a new pass-count measurement
 * (e.g. "Pass Count 1", "Pass Count 2").
 */
export function getNextPassCountLabel(measurements: Measurement[]): string {
  return getLabelFromMarkup({ name: 'passCountMeasurement' }, measurements);
}

function isMarkupComplete(markup: Markup): boolean {
  if (markup instanceof PointMarkup) {
    return markup.pick != null;
  }
  if (
    markup instanceof Web3DMeasurement ||
    markup instanceof AngleMeasurement
  ) {
    return markup.startPick?.point != null && markup.endPick?.point != null;
  }
  if (markup instanceof PolylineMeasurement) {
    return markup.picks.length >= MIN_POLYLINE_POINTS;
  }
  return true;
}

export function convertMarkupToMeasurement(
  markup: Markup,
  label: string,
  pointMeasurementSettings: PointMeasurementSettings
): Measurement | null {
  // web3d fires markupmodified before a markup is fully picked; wait for a later event.
  if (!isMarkupComplete(markup)) {
    return null;
  }

  switch (markup.name) {
    case 'pointMarkup':
      return createPointMeasurement(markup, label, pointMeasurementSettings);
    case 'measurement':
      return createLineMeasurement(markup, label);
    case 'angleMeasurement':
      return createAngleMeasurement(markup, label);
    case 'alignmentMeasurement':
      return createAlignmentMeasurement(markup, label);
    case 'polylineMeasurement':
      return createPolylineMeasurement(markup, label);
    case 'polygonMeasurement':
      return createAreaMeasurement(markup, label);
    default:
      throw new UnreachableCaseError(markup.name as never);
  }
}

export function processMarkup(markup: ConnectMarkup): MeasurementMarkup | null {
  switch (markup.type) {
    case 'measure':
      return markup as MeasureMarkup;
    case 'measure_with_pick':
      return markup as MeasureWithPickMarkup;
    case 'angle':
      return markup as AngleMarkup;
    default:
      console.warn(`Measurement type '${markup.type}' is not supported`);
      return null;
  }
}

function createAlignmentMeasurement(
  markup: Markup,
  label: string
): Measurement {
  if (
    !(markup instanceof AlignmentMeasurement) ||
    !markup.groundPick.point ||
    !markup.alignmentPick.point ||
    !markup.pointPick.point
  ) {
    throw Error(
      'Invalid markup type or missing properties for alignment markup'
    );
  }

  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'alignment',
    value: markup.distance,
    ground: markup.groundPick.point,
    alignment: markup.alignmentPick.point,
    point: markup.pointPick.point,
    label,
  };
}

function createPointMeasurement(
  markup: Markup,
  label: string,
  pointMeasurementSettings: PointMeasurementSettings
): Measurement {
  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'point',
    x: markup.position.x,
    y: markup.position.y,
    z: markup.position.z,
    color: web3dColorToRgb(markup.color),
    showXYCoordinates: pointMeasurementSettings.showXYCoordinates,
    showZCoordinate: pointMeasurementSettings.showZCoordinate,
    label,
  };
}

function createLineMeasurement(markup: Markup, label: string): Measurement {
  if (
    !(markup instanceof Web3DMeasurement) ||
    !markup.startPick.point ||
    !markup.endPick.point
  ) {
    throw Error('Invalid markup type or missing properties for line markup');
  }

  const mainLine = markup.getMainLine();
  // Must clone — without it:
  // 1. user drags endpoint → web3d updates Vector3 in-place
  // 2. measurement.startPoint now has the new value too (same object)
  // 3. syncLineMeasurement compares old vs new → both equal → no sync
  const startPoint = mainLine.start.clone();
  const endPoint = mainLine.end.clone();

  const value = calculateDistance(startPoint, endPoint);
  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'line',
    value,
    // TODO: do we need snapping here.
    // If yes make it consistent across measurements.
    startPoint,
    endPoint,
    color: web3dColorToRgb(markup.color),
    label,
  };
}

export function createAngleMeasurement(
  markup: Markup,
  label: string
): Measurement {
  if (
    !(markup instanceof AngleMeasurement) ||
    !markup.startPick.point ||
    !markup.endPick.point ||
    markup.startPick.snapType == undefined ||
    markup.endPick.snapType == undefined
  ) {
    throw Error('Invalid markup type or missing properties for angle markup');
  }

  const startPoint = markup.startPick.point.clone();
  const endPoint = markup.endPick.point.clone();
  const middlePoint = (markup.secondPick?.point ?? markup.centrePoint).clone();

  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'angle',
    value: markup.angleInRadians,
    startPoint,
    endPoint,
    middlePoint,
    snapStart: markup.startPick.snapType,
    snapEnd: markup.endPick.snapType,
    color: web3dColorToRgb(markup.color),
    label,
  };
}

export function clonePicks(picks: SnapPick[]): SnapPick[] {
  return picks.map((pick) => ({
    ...pick,
    point: pick.point?.clone(),
  }));
}

function createPolylineMeasurement(markup: Markup, label: string): Measurement {
  const value = (markup as PolylineMeasurement).value();
  if (
    !(markup instanceof PolylineMeasurement) ||
    markup.picks.length < MIN_POLYLINE_POINTS
  ) {
    throw Error(
      'Invalid markup type or missing properties for polyline markup'
    );
  }

  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'polyline',
    value,
    picks: clonePicks(markup.picks),
    color: web3dColorToRgb(markup.color),
    label,
    showSegmentDistances: markup.showSegmentDistances,
    showSegmentSlopes: markup.showSegmentSlopes,
    showHorizontalMeasurements: markup.showHorizontalMeasurements,
    showVerticalMeasurements: markup.showVerticalMeasurements,
  };
}

function createAreaMeasurement(
  markup: Markup,
  label: string
): Measurement | null {
  if (!(markup instanceof PolygonMeasurement)) {
    throw Error('Invalid markup type or missing properties for polygon markup');
  }
  if (!markup.isClosed) {
    return null;
  }
  return {
    id: uuid.v4(),
    markups: [markup],
    type: 'area',
    picks: clonePicks(markup.picks),
    color: web3dColorToRgb(markup.color),
    label,
    lineThickness: markup.lineThickness,
    lineStyle: markup.lineStyle,
    fillOpacity: markup.fillOpacity,
  };
}

export function createVolumeMeasurement(
  parameters: VolumeMeasurementParameters,
  results: VolumeMeasurementResults,
  label: string
): VolumeMeasurementType {
  const volumeMeasurement: VolumeMeasurementType = {
    id: parameters.id ?? uuid.v4(),
    markups: [],
    type: 'volume',
    label,
    parameters,
    result: results,
    color: { r: 0, g: 0, b: 0 },
  };

  return volumeMeasurement;
}

export function createPassCountMeasurement(
  parameters: PassCountMeasurementParameters,
  label: string,
  results?: PassCountMeasurementResults
): PassCountMeasurementType {
  return {
    id: parameters.id ?? uuid.v4(),
    markups: [],
    type: 'passCount',
    label,
    parameters,
    result: results,
    color: { r: 0, g: 0, b: 0 },
  };
}

/** Minimum distinct vertices for a polygon boundary ring. */
const MIN_POLYGON_BOUNDARY_VERTICES = 3;

/** Index of Z in GeoJSON-style [x, y, z] coordinate tuples. */
const COORD_Z = 2;

/**
 * Squared distance below which first and last ring vertices are treated as one
 * (closed GeoJSON polygon ring).
 */
const CLOSED_RING_VERTEX_EPSILON_SQ = 1e-20;

/**
 * Converts a GeoJSON-style `boundaryInclusive` payload (from volume API / Civil flow)
 * into snap picks for polygon measurement markup. Returns undefined when there is
 * no drawable boundary or not enough vertices.
 */
export function boundaryInclusiveToSnapPicks(
  boundaryInclusive: VolumeMeasurementParameters['boundaryInclusive']
): SnapPick[] | undefined {
  if (boundaryInclusive == null || typeof boundaryInclusive !== 'object') {
    return undefined;
  }
  const asRecord = boundaryInclusive as Record<string, unknown>;
  if (asRecord['type'] !== 'Polygon') {
    return undefined;
  }
  const coordinates = asRecord['coordinates'];
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return undefined;
  }
  const ring = coordinates[0] as unknown;
  if (!Array.isArray(ring) || ring.length === 0) {
    return undefined;
  }

  const coordLen3D = 3;
  const points: Vector3[] = [];
  for (const coord of ring) {
    if (!Array.isArray(coord) || coord.length < coordLen3D) {
      return undefined;
    }
    points.push(
      new Vector3(
        coord[0] as number,
        coord[1] as number,
        coord[COORD_Z] as number
      )
    );
  }

  let trimmed = points;
  if (points.length >= MIN_POLYGON_BOUNDARY_VERTICES + 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.distanceToSquared(last) < CLOSED_RING_VERTEX_EPSILON_SQ) {
      trimmed = points.slice(0, -1);
    }
  }

  if (trimmed.length < MIN_POLYGON_BOUNDARY_VERTICES) {
    return undefined;
  }

  return trimmed.map((point) => ({
    snapType: SnapType.POINT as number,
    point,
  }));
}

export function transformTcMarkupToMeasurement(
  tcMarkup: MeasurementMarkup
): Measurement | null {
  if (!tcMarkup.markupId) {
    console.warn('Invalid Connect markup: missing markupId', tcMarkup);
    return null;
  }

  const id = tcMarkup.markupId;

  switch (tcMarkup.type) {
    case 'measure': {
      const position = convertConnectPointToViewerPoint(
        tcMarkup.positionX,
        tcMarkup.positionY,
        tcMarkup.positionZ
      );

      return {
        id,
        type: 'point',
        markups: [],
        x: position.x,
        y: position.y,
        z: position.z,
        color: tcMarkup.color,
      };
    }

    case 'measure_with_pick': {
      const startPoint = convertConnectPointToViewerPoint(
        tcMarkup.start.positionX,
        tcMarkup.start.positionY,
        tcMarkup.start.positionZ
      );
      const endPoint = convertConnectPointToViewerPoint(
        tcMarkup.end.positionX,
        tcMarkup.end.positionY,
        tcMarkup.end.positionZ
      );

      const value = calculateDistance(startPoint, endPoint);
      return {
        id,
        type: 'line',
        markups: [],
        startPoint,
        endPoint,
        value,
        color: tcMarkup.color,
      };
    }

    case 'angle': {
      const startPoint = convertConnectPointToViewerPoint(
        tcMarkup.positionX,
        tcMarkup.positionY,
        tcMarkup.positionZ
      );
      const endPoint = convertConnectPointToViewerPoint(
        tcMarkup.start.positionX,
        tcMarkup.start.positionY,
        tcMarkup.start.positionZ
      );
      const middlePoint = convertConnectPointToViewerPoint(
        tcMarkup.end.positionX,
        tcMarkup.end.positionY,
        tcMarkup.end.positionZ
      );

      const startToEnd = new Vector3().subVectors(endPoint, startPoint);
      const startToMiddle = new Vector3().subVectors(middlePoint, startPoint);
      const angleInRadians = startToEnd.angleTo(startToMiddle);

      return {
        id,
        type: 'angle',
        markups: [],
        startPoint,
        endPoint,
        middlePoint,
        value: angleInRadians,
        snapStart: SnapType.POINT as number,
        snapEnd: SnapType.POINT as number,
        color: tcMarkup.color,
      };
    }

    default:
      throw new UnreachableCaseError(tcMarkup);
  }
}

export function pointPositionFormatter(
  position: Vector3,
  viewer: Web3DViewer,
  showXYCoordinates: boolean = true,
  showZCoordinate: boolean = true,
  label: string = ''
): string {
  const customLengthFormatter = viewer.settings.customLengthFormatter;
  const parts: string[] = [];

  if (label) {
    parts.push(label);
  }

  if (showXYCoordinates) {
    parts.push(
      `X: ${String(customLengthFormatter ? customLengthFormatter(position.x) : position.x)}`
    );
    parts.push(
      `Y: ${String(customLengthFormatter ? customLengthFormatter(position.y) : position.y)}`
    );
  }
  if (showZCoordinate) {
    parts.push(
      `Z: ${String(customLengthFormatter ? customLengthFormatter(position.z) : position.z)}`
    );
  }
  return parts.join('\n');
}

/**
 * Shared by the point edit flow and view-restore so 3D labels stay in sync.
 */
export function applyPointLabel(
  markup: PointMarkup,
  viewer: Web3DViewer,
  label: string | undefined,
  showXYCoordinates: boolean,
  showZCoordinate: boolean
): void {
  markup.positionFormatter = (position) =>
    pointPositionFormatter(
      position,
      viewer,
      showXYCoordinates,
      showZCoordinate,
      label
    );
}

export function slopeFormatter(
  vertical: number,
  horizontal: number | undefined,
  slopeFormat: SlopeFormat
): string {
  const horizontalValue = horizontal ?? 0;
  const slopeRatio = vertical / horizontalValue;

  return SlopeFormatter.toDisplay({
    measuredValue: slopeRatio,
    currentUnits: 'ratio',
    settings: { format: slopeFormat },
  });
}

/**
 * Convert a Web3D measurement to Connect Markup format
 */
export function convertMeasurementToConnectMarkup(
  measurement: Measurement
): MeasurementMarkup | null {
  switch (measurement.type) {
    case 'point':
      return {
        type: 'measure',
        positionX: convertMetersToMillimeters(measurement.x),
        positionY: convertMetersToMillimeters(measurement.y),
        positionZ: convertMetersToMillimeters(measurement.z),
        position2X: convertMetersToMillimeters(measurement.x),
        position2Y: convertMetersToMillimeters(measurement.y),
        position2Z: convertMetersToMillimeters(measurement.z),
        color: ensureAlpha(measurement.color),
      } as MeasureMarkup;

    case 'line':
      return {
        type: 'measure_with_pick',
        start: {
          type: 'point',
          positionX: convertMetersToMillimeters(measurement.startPoint.x),
          positionY: convertMetersToMillimeters(measurement.startPoint.y),
          positionZ: convertMetersToMillimeters(measurement.startPoint.z),
        },
        end: {
          type: 'point',
          positionX: convertMetersToMillimeters(measurement.endPoint.x),
          positionY: convertMetersToMillimeters(measurement.endPoint.y),
          positionZ: convertMetersToMillimeters(measurement.endPoint.z),
        },
        unitType: 'Metric',
        quantityUnit: 'millimeter',
        quantityUnitFormat: 'mm',
        color: ensureAlpha(measurement.color),
      } as MeasureWithPickMarkup;

    case 'angle':
      return {
        type: 'angle',
        positionX: convertMetersToMillimeters(measurement.startPoint.x),
        positionY: convertMetersToMillimeters(measurement.startPoint.y),
        positionZ: convertMetersToMillimeters(measurement.startPoint.z),
        start: {
          type: 'point',
          positionX: convertMetersToMillimeters(measurement.endPoint.x),
          positionY: convertMetersToMillimeters(measurement.endPoint.y),
          positionZ: convertMetersToMillimeters(measurement.endPoint.z),
        },
        end: {
          type: 'point',
          positionX: convertMetersToMillimeters(measurement.middlePoint.x),
          positionY: convertMetersToMillimeters(measurement.middlePoint.y),
          positionZ: convertMetersToMillimeters(measurement.middlePoint.z),
        },
        color: ensureAlpha(measurement.color),
      } as AngleMarkup;

    case 'alignment':
    case 'polyline':
    case 'volume':
    case 'passCount':
    case 'area':
      console.warn(`Measurement type '${measurement.type}' not supported`);
      return null;

    default:
      throw new UnreachableCaseError(measurement);
  }
}

/**
 * Helper function to convert a 3D coordinate number array to a Vector3 array.
 * Used for converting raw GeoJSON coordinate lists (as `number[][]`)
 * to a Vector3 array for polygon construction
 * @param coordinates Array of 3D coordinate tuples
 * @returns Vector3 array, or undefined if input array was malformed
 */
export function coordNumberArrayToVector3Array(
  coordinates: number[][]
): Vector3[] | undefined {
  if (coordinates.length === 0) {
    return undefined;
  }
  const coordinateLen3D = 3;
  const points: Vector3[] = [];
  for (let i = 0; i < coordinates.length; i++) {
    const point = coordinates[i];
    if (point.length !== coordinateLen3D) {
      return undefined;
    }
    // eslint-disable-next-line no-magic-numbers
    points.push(new Vector3(point[0], point[1], point[2]));
  }
  return points;
}

export function getBoundaryPointsFromPolygonMarkup(
  items: Measurement[],
  pickId: number | string
): Vector3[] | undefined {
  const areaMeasurement = items.find(
    (m) =>
      isAreaMeasurement(m) && m.markups.some((markup) => markup.id == pickId)
  );
  if (areaMeasurement && isAreaMeasurement(areaMeasurement)) {
    return areaMeasurement.picks.flatMap((p) =>
      p.point != null ? [new Vector3(p.point.x, p.point.y, p.point.z)] : []
    );
  }
  const volumeMeasurement = items.find(
    (m) =>
      isVolumeMeasurement(m) && m.markups.some((markup) => markup.id == pickId)
  );
  if (!volumeMeasurement || !isVolumeMeasurement(volumeMeasurement)) {
    return undefined;
  }
  const snapPicks = boundaryInclusiveToSnapPicks(
    volumeMeasurement.parameters.boundaryInclusive
  );
  if (snapPicks && snapPicks.length > 0) {
    return snapPicks.flatMap((p) => (p.point != null ? [p.point] : []));
  }
  const markup = volumeMeasurement.markups.find((m) => m.id == pickId);
  const linePoints =
    markup &&
    'linePoints' in markup &&
    Array.isArray((markup as { linePoints?: Vector3[] }).linePoints)
      ? (markup as { linePoints: Vector3[] }).linePoints
      : undefined;
  if (!linePoints?.length) {
    return undefined;
  }
  return linePoints.map((p) => new Vector3(p.x, p.y, p.z));
}
